import { z } from 'zod';

/**
 * F23 — Variable Commission Engine frozen API contracts.
 *
 * See plans/23_F23_commission_engine.md (Wave 1 / Task 1.2) for the
 * founder-frozen shape of every Zod schema and type alias below. Wave 2
 * sub-agents (2A backend / 2B B2B portal / 2C admin web) develop against
 * these contracts in parallel — DO NOT rename, drop, or add fields without
 * escalating to the orchestrator first (contract-change protocol).
 *
 * F23 ships the per-venue commission rate (append-only history), the
 * commission-records ledger (one row per accrual / reversal), and the
 * monthly statement state machine. F23 is the first downstream consumer
 * of F20's `PaymentEventConsumer` interface (`payment-events.ts`); F20's
 * `PaymentMarkService` calls into the F23 implementation inside the same
 * Drizzle transaction as its payment_marks row so ledger writes are
 * atomic with payment-state mutations.
 *
 * All monetary fields are USD minor units (integer, cents).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/**
 * Ledger entry kind. `accrual` rows have positive `commissionAmount` (and
 * positive `baseAmount`); `reversal` rows have negative `commissionAmount`
 * (and negative `baseAmount`). Per Decision 6, reversals are written as
 * separate rows — accruals are never UPDATEd. The ledger nets via SUM.
 */
export const COMMISSION_RECORD_KINDS = ['accrual', 'reversal'] as const;
export type CommissionRecordKind = (typeof COMMISSION_RECORD_KINDS)[number];

/**
 * Which downstream event led to this commission row. `payment.marked_paid`
 * is the only accrual trigger at MVP; the three reversal triggers cover the
 * F20 refund + dispute paths and the F17 admin force-transition path.
 *
 * `admin_adjustment` is reserved for the rare incident-response path where
 * admin manually compensates for a settled-statement void (Decision 11) —
 * F23 ships the trigger but does NOT expose a routine admin-adjustment UI;
 * F44 financial dashboard owns that surface.
 */
export const COMMISSION_TRIGGER_EVENTS = [
  'payment.marked_paid',
  'payment.marked_refunded',
  'payment.dispute_resolved_customer_correct',
  'admin_adjustment',
] as const;
export type CommissionTriggerEvent = (typeof COMMISSION_TRIGGER_EVENTS)[number];

/**
 * Statement state machine (Decision 8). `draft → issued → settled` with
 * `void` as a terminal escape hatch for incident response.
 */
export const COMMISSION_STATEMENT_STATUSES = ['draft', 'issued', 'settled', 'void'] as const;
export type CommissionStatementStatus = (typeof COMMISSION_STATEMENT_STATUSES)[number];

/**
 * How a statement transitioned from `draft → issued`. `worker_auto` is the
 * normal path (the F23 worker auto-issues at period close); `admin_manual`
 * covers the rare admin-manual-issue path (e.g. closing a venue's account
 * before period close).
 */
export const COMMISSION_STATEMENT_ISSUE_SOURCES = ['worker_auto', 'admin_manual'] as const;
export type CommissionStatementIssueSource = (typeof COMMISSION_STATEMENT_ISSUE_SOURCES)[number];

/**
 * F35 — entity discriminator on `commission_records`. `'venue'` is the F23
 * default (a venue-side commission row keyed off a `payment_marks` row);
 * `'trainer'` is the F35 trainer-side row (keyed off `dedup_key`, no
 * `payment_marks`). Venue rows carry `venueId`+`paymentMarkId`; trainer rows
 * carry `trainerUserId`.
 */
export const COMMISSION_ENTITY_KINDS = ['venue', 'trainer'] as const;
export type CommissionEntityKind = (typeof COMMISSION_ENTITY_KINDS)[number];

/**
 * F35 — source discriminator on `commission_statements`. `'venue'` (F23
 * venue commission), `'trainer_commission'` (F35 per-session trainer
 * commission), `'trainer_flat'` (F35 fixed monthly trainer subscription
 * fee). The admin statements queue filters on this; the SAME state machine
 * (`draft → issued → settled → void`) + settlement path apply to all three.
 */
// F38 appends the two `venue_*` subscription-invoice sources
// (`venue_subscription` = subscription_only fee invoice; `venue_hybrid` = the
// subscription-fee invoice for a hybrid venue, which ALSO accrues commission
// through the normal `venue` source). Additive — keep the existing order so
// downstream switch statements stay exhaustive.
// F39 appends `venue_placement` — the monthly featured-placement out-of-pocket
// invoice (one per venue per calendar month; net_charge of placements expiring
// that month, line items via `placements.billed_statement_id`). Disjoint
// partial-unique + the `placement_fee_amount` column mirror the F35/F38 flat-
// invoice precedent. Additive — keep the existing order.
// F41 appends `tournament_listing` — the monthly tournament-listing-fee invoice
// (one per ORGANIZER b2b_account per calendar month; the per-published-tournament
// flat fee accumulates into `commission_statements.tournament_listing_fee_amount`,
// `records_count=0`, each tournament stamped via `tournaments.billed_statement_id`).
// Keyed by the disjoint `commission_statements.organizer_b2b_account_id` so an
// organizer's listing statement never collides with any venue/subscription/
// placement statement for the same period. Additive — keep the existing order so
// downstream switch statements stay exhaustive.
export const COMMISSION_SOURCES = [
  'venue',
  'trainer_commission',
  'trainer_flat',
  'venue_subscription',
  'venue_hybrid',
  'venue_placement',
  'tournament_listing',
] as const;
export type CommissionSource = (typeof COMMISSION_SOURCES)[number];

/**
 * Decision 1 — platform-wide IANA timezone for monthly period boundary
 * computation. All MVP venues are in Lebanon. MENA expansion will widen
 * this to read `venues.timezone` (already present per F16 schema); F23
 * service layer reads `venues.timezone` defensively at runtime so this
 * const acts as a fallback.
 */
export const STATEMENT_PERIOD_TIMEZONE = 'Asia/Beirut' as const;

/**
 * Decision 12 — platform default commission rate at F23 launch (10.00%).
 * The 0018 migration back-fills every existing venue with this rate so
 * F23's accrual path never reads a missing-row error. The service layer
 * also defence-in-depths to this constant if a row is missing in test
 * setups (with a pino warn).
 */
export const PLATFORM_DEFAULT_RATE_BASIS_POINTS = 1000 as const;

/**
 * Decision 12 — admins can set rates in [0, 5000] basis points (0%–50%).
 * 0 is legitimate (waived commission); above 50% is rejected as an
 * almost-certain admin typo.
 */
export const MIN_RATE_BASIS_POINTS = 0 as const;
export const MAX_RATE_BASIS_POINTS = 5000 as const;

// ─── Entity shapes (read-side projections) ───────────────────────────────────

/**
 * One row of the append-only `venue_commission_rates` history (Decision 2).
 * The "active rate" for a venue is the most recent row whose `effectiveAt`
 * is ≤ now(). `setByAdminDisplayName` is resolved at read time by a JOIN
 * against `admin_users`; null after the admin is hard-deleted (FK SET NULL).
 */
export type VenueCommissionRate = {
  readonly id: string;
  readonly venueId: string;
  /** USD minor units. 0–5000 (0%–50%). */
  readonly rateBasisPoints: number;
  /** ISO 8601. The moment from which this rate applies forward. */
  readonly effectiveAt: string;
  readonly setByAdminId: string | null;
  readonly setByAdminDisplayName: string | null;
  readonly reason: string | null;
  readonly createdAt: string;
};

/**
 * One row of the append-only `commission_records` ledger (Decision 3, 6, 7, 9).
 * Idempotency via UNIQUE on `paymentMarkId` (Decision 9). Snapshot semantics:
 * every row carries its own `rateBasisPoints` + `baseAmount` + `baseCurrency`
 * + `fxRateId` at accrual time. Historical statements remain reproducible
 * across forward-only rate changes.
 *
 * `commissionAmount` is signed — positive for accruals, negative for
 * reversals. `baseAmount` follows the same sign convention.
 */
export type CommissionRecord = {
  readonly id: string;
  readonly bookingId: string;
  /** F35 — 'venue' (F23 default) | 'trainer' (F35). */
  readonly entityKind: CommissionEntityKind;
  /** NULL for trainer-entity rows (which carry `trainerUserId` instead). */
  readonly venueId: string | null;
  /** NULL for venue-entity rows (F35). */
  readonly trainerUserId: string | null;
  /** NULL for trainer-entity rows (no `payment_marks` row; idempotency via `dedup_key`). */
  readonly paymentMarkId: string | null;
  readonly kind: CommissionRecordKind;
  readonly triggerEvent: CommissionTriggerEvent;
  /** USD minor units. Signed. */
  readonly baseAmount: number;
  readonly baseCurrency: string;
  readonly fxRateId: string;
  readonly rateBasisPoints: number;
  /** USD minor units. Signed. */
  readonly commissionAmount: number;
  /** NULL until the worker attaches this record at period close. */
  readonly statementId: string | null;
  /** ISO 8601. The source payment_marks.created_at — records sort into
   * statements by THIS timestamp regardless of when the commission row
   * itself was written. */
  readonly paymentMarkedAt: string;
  readonly countryCode: string;
  readonly createdAt: string;
};

/**
 * Booking preview embedded into each commission record on the statement-detail
 * page so the B2B portal can render `{sport @ sub-venue, start–end}` without
 * a second fetch.
 */
export type CommissionRecordBookingPreview = {
  readonly id: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly sportName: string;
  readonly subVenueName: string | null;
};

/**
 * `CommissionRecord` widened with the booking preview. Returned by the
 * statement-detail endpoint (and by the admin records list endpoint).
 */
export type CommissionRecordDetail = CommissionRecord & {
  readonly booking: CommissionRecordBookingPreview;
};

/**
 * One row of `commission_statements`. State machine + lifecycle audit fields.
 * `gross_paid_amount_sum`, `commission_amount_sum`, `records_count` are
 * derived from attached records; they're persisted on the statement row so
 * the venue portal page can render KPIs without aggregating the ledger.
 *
 * `issuedAt` + `issuedVia` set on `draft → issued`. `settledAt` +
 * `settledByAdminId` + `settledReference` + `settledNotes` set on
 * `issued → settled`. `voidedAt` + `voidedReason` set on
 * `(issued | settled) → void`.
 */
export type CommissionStatement = {
  readonly id: string;
  /** F35 — 'venue' (F23) | 'trainer_commission' | 'trainer_flat' (F35). */
  readonly source: CommissionSource;
  /** NULL for trainer-source statements (which carry `trainerUserId` instead). */
  readonly venueId: string | null;
  /** NULL for venue-source statements (F35). */
  readonly trainerUserId: string | null;
  /** Fixed monthly fee for `source='trainer_flat'` (USD minor units); NULL otherwise (F35). */
  readonly flatFeeAmount: number | null;
  /** Trainer-invoice due date (ISO 8601); drives the overdue worker. NULL for venue statements (F35). */
  readonly dueAt: string | null;
  /** ISO 8601 UTC. First instant of the calendar month in Asia/Beirut. */
  readonly periodStart: string;
  /** ISO 8601 UTC. First instant of the NEXT calendar month in Asia/Beirut. */
  readonly periodEnd: string;
  readonly status: CommissionStatementStatus;
  readonly grossPaidAmountSum: number;
  readonly commissionAmountSum: number;
  readonly recordsCount: number;
  readonly issuedAt: string | null;
  readonly issuedVia: CommissionStatementIssueSource | null;
  readonly issuedByAdminId: string | null;
  readonly issuedByAdminDisplayName: string | null;
  readonly settledAt: string | null;
  readonly settledByAdminId: string | null;
  readonly settledByAdminDisplayName: string | null;
  readonly settledReference: string | null;
  readonly settledNotes: string | null;
  readonly voidedAt: string | null;
  readonly voidedByAdminId: string | null;
  readonly voidedReason: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * `CommissionStatement` widened with the paginated attached records. Returned
 * by the statement-detail endpoints (venue + admin).
 *
 * `records` is the flat slice (per the current page); `recordsPagination`
 * carries the `{ page, perPage, total }` triple so the UI can page through
 * larger statements without re-shaping the response.
 *
 * F23 Wave 3 reconciled the Wave-2A/-2B/-2C contract drift here: the single
 * canonical shape is `data: CommissionStatementDetail` with `records` as a
 * flat array and `recordsPagination` as the sidecar metadata. Both venue
 * (B2B portal) and admin web consume identical shapes.
 */
export type CommissionStatementDetail = CommissionStatement & {
  readonly records: readonly CommissionRecordDetail[];
  readonly recordsPagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
};

/**
 * The "in-flight" pre-statement summary returned by `GET /me/venues/:id/commission`.
 * Computes the current calendar month's bounds + aggregates over the unattached
 * records for that month. Used by the B2B portal's commission page KPIs.
 */
export type OpenAccrualSummary = {
  readonly venueId: string;
  readonly currentPeriodStart: string;
  readonly currentPeriodEnd: string;
  readonly recordsCount: number;
  readonly grossPaidAmountSum: number;
  readonly commissionAmountSum: number;
};

// ─── Zod input schemas ───────────────────────────────────────────────────────

/**
 * `PUT /admin/commission-rates/:venueId` body — admin rate change. Service
 * layer additionally rejects:
 *   - same-as-current rate → 422 `COMMISSION_RATE_NO_CHANGE`
 *   - past `effectiveAt` → 422 `COMMISSION_RATE_OUT_OF_BOUNDS`
 *
 * `effectiveAt` is optional; when omitted the server uses `now()`. Audit-only
 * `reason` (1–500 chars) is required by the API (Decision 2 — every rate
 * change must carry a human justification visible in the admin audit log).
 */
export const SetVenueCommissionRateInputSchema = z
  .object({
    rateBasisPoints: z.number().int().min(MIN_RATE_BASIS_POINTS).max(MAX_RATE_BASIS_POINTS),
    reason: z.string().trim().min(1).max(500),
    effectiveAt: z.string().datetime().optional(),
  })
  .strict();
export type SetVenueCommissionRateInput = z.infer<typeof SetVenueCommissionRateInputSchema>;

/**
 * `POST /admin/commission-statements/:id/settle` body — admin marks an issued
 * statement settled after off-platform invoice receipt. Mirrors F20's
 * mark-as-paid pattern.
 *
 * `settledReference` (e.g. Whish transaction id) required 1–120 chars;
 * `settledNotes` optional ≤500 chars.
 */
export const MarkStatementSettledInputSchema = z
  .object({
    settledReference: z.string().trim().min(1).max(120),
    settledNotes: z.string().trim().max(500).optional(),
  })
  .strict();
export type MarkStatementSettledInput = z.infer<typeof MarkStatementSettledInputSchema>;

/**
 * `POST /admin/commission-statements/:id/void` body — admin voids a statement
 * for incident response (Decision 11). Mandatory `reason` 1–500 chars is
 * captured in `commission_statements.voided_reason` AND in the F06 admin
 * audit log payload.
 */
export const VoidStatementInputSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type VoidStatementInput = z.infer<typeof VoidStatementInputSchema>;

// ─── Error codes ─────────────────────────────────────────────────────────────

/**
 * F23 error vocabulary. Every API error response returns one of these as
 * `error.code`. Service layer maps to HTTP status codes:
 *
 *   - `*_OUT_OF_BOUNDS` / `*_NOT_DRAFT` / `*_NOT_ISSUED` / `*_ALREADY_SETTLED`
 *     / `*_ALREADY_VOIDED` / `*_NO_CHANGE` / `*_INVALID` → 422
 *   - `*_NOT_FOUND` → 404
 *   - `*_DUPLICATE` → 409 (idempotent replay; consumer treats as no-op)
 *   - `*_REQUIRES_REASON` → 400 (Zod-layer rejection, but reserved here for
 *     consistency with F17 error vocabulary)
 */
export const COMMISSION_ERROR_CODES = [
  'COMMISSION_RATE_OUT_OF_BOUNDS',
  'COMMISSION_RATE_NO_CHANGE',
  'COMMISSION_RATE_NOT_FOUND',
  'COMMISSION_RECORD_DUPLICATE',
  'COMMISSION_STATEMENT_NOT_FOUND',
  'COMMISSION_STATEMENT_NOT_ISSUED',
  'COMMISSION_STATEMENT_ALREADY_SETTLED',
  'COMMISSION_STATEMENT_ALREADY_VOIDED',
  'COMMISSION_STATEMENT_NOT_DRAFT',
  'COMMISSION_PERIOD_INVALID',
] as const;
export type CommissionErrorCode = (typeof COMMISSION_ERROR_CODES)[number];
